import React, { useEffect, useState } from 'react';
import { waLink, waLinkProps } from '../mock';
import WhatsAppIcon from './WhatsAppIcon';
import { trackWhatsAppClick } from '../lib/analytics';

const PARTICLES = Array.from({ length: 26 }, (_, i) => ({
  id: i,
  left: (i * 37 + 13) % 100,
  size: 2 + ((i * 7) % 4),
  delay: (i * 1.7) % 14,
  duration: 11 + ((i * 3) % 9),
  opacity: 0.25 + ((i * 13) % 50) / 100,
}));

const Hero = ({ product, onOpenProduct }) => {
  const [activated, setActivated] = useState(false);
  const [webpReady, setWebpReady] = useState(false);

  // Preload animated webp so the crossfade is imperceptible
  useEffect(() => {
    if (!product.animation) return;
    const img = new Image();
    img.onload = () => setWebpReady(true);
    img.src = product.animation;
  }, [product]);

  // First scroll triggers the poster -> webp transition
  useEffect(() => {
    if (!product.animation) return;
    const onScroll = () => {
      if (window.scrollY > 10) setActivated(true);
    };
    const onWheel = () => setActivated(true);
    const onTouch = () => setActivated(true);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true, once: true });
    window.addEventListener('touchmove', onTouch, { passive: true, once: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchmove', onTouch);
    };
  }, [product]);

  const showAnimation = activated && webpReady && !!product.animation;

  return (
    <section className="q-hero q-hero--banner" data-testid="hero-section">
      {/* Layered media: poster + animated webp crossfade */}
      <div className="q-hero__media">
        <img
          src={product.image}
          alt={product.name}
          className={`q-hero__frame ${showAnimation ? 'q-hero__frame--hidden' : ''}`}
          data-testid="hero-poster"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
        {product.animation && (
          <img
            src={product.animation}
            alt=""
            aria-hidden="true"
            className={`q-hero__frame q-hero__frame--anim ${showAnimation ? 'q-hero__frame--shown' : ''}`}
            data-testid="hero-animation"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
        )}
        <div className="q-hero__vignette" />
        <div className="q-hero__haze" />
      </div>

      {/* Floating gold particles */}
      <div className="q-hero__particles" aria-hidden="true">
        {PARTICLES.map((p) => (
          <span
            key={p.id}
            className="q-particle"
            style={{
              left: `${p.left}%`,
              width: p.size,
              height: p.size,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              opacity: p.opacity,
            }}
          />
        ))}
      </div>

      {/* Left — piece name */}
      <div className="q-hero__side q-hero__side--left">
        <div className="q-hero__brand-eyebrow">
          <span className="q-hero__brand-rule" />
          Pieza insignia
        </div>
        <h2 className="q-hero__piece" data-testid="hero-piece-name">{product.name}</h2>
      </div>

      {/* Right — material + description */}
      <div className="q-hero__side q-hero__side--right">
        <div className="q-hero__piece-label">{product.material}</div>
        <div className="q-hero__rule" />
        <p className="q-hero__desc">{product.description}</p>
      </div>

      {/* Bottom corner CTAs */}
      <button
        type="button"
        onClick={() => onOpenProduct && onOpenProduct(product)}
        className="q-btn q-btn--ghost q-hero__cta q-hero__cta--left"
        data-testid="hero-cta-detail"
      >
        Ver detalle
      </button>
      <a
        href={waLink(`Hola QUINLATE, me interesa cotizar: ${product.name}.`)}
        {...waLinkProps}
        className="q-btn q-btn--gold q-btn--wa q-hero__cta q-hero__cta--right"
        data-testid="hero-cta-whatsapp"
        aria-label="Cotizar por WhatsApp"
        onClick={() => trackWhatsAppClick('hero', { product: product.id })}
      >
        <WhatsAppIcon size={24} />
      </a>

      {/* Scroll hint */}
      {product.animation && (
        <div className={`q-hero__scrollhint ${activated ? 'q-hero__scrollhint--hidden' : ''}`} aria-hidden="true">
          <span className="q-hero__scrollhint-line" />
          <span className="q-hero__scrollhint-text">Scroll</span>
        </div>
      )}
    </section>
  );
};

export default Hero;
